import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  Sparkles, AlertTriangle, ArrowUp, ArrowDown, ArrowLeft, ArrowRight,
  ZoomIn, ZoomOut, Check, RefreshCw,
} from "lucide-react";

const OUTPUT_SIZE = 512;       // final square export size
const NUDGE_PX_FRAC = 0.06;    // arrow step = 6% of crop size
const ZOOM_STEP = 0.1;

// Lazy CDN fallback — loaded only when window.FaceDetector is missing (Safari / Firefox / iOS).
// ~190 KB model + ~120 KB script, then cached by the browser for all future sessions.
const FACEAPI_SCRIPT = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
const FACEAPI_WEIGHTS = "https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights";
let _faceapiPromise = null;
async function loadFaceApi() {
  if (window.faceapi) return window.faceapi;
  if (_faceapiPromise) return _faceapiPromise;
  _faceapiPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = FACEAPI_SCRIPT;
    s.async = true;
    s.onload = async () => {
      try {
        await window.faceapi.nets.tinyFaceDetector.loadFromUri(FACEAPI_WEIGHTS);
        resolve(window.faceapi);
      } catch (e) { reject(e); }
    };
    s.onerror = () => reject(new Error("face-api CDN unreachable"));
    document.head.appendChild(s);
  });
  return _faceapiPromise;
}

/**
 * ProfilePhotoEditor — modal that takes a raw File, shows a live square-crop
 * preview, optionally uses window.FaceDetector to auto-centre the crop on a
 * detected face, and returns a cropped JPEG blob via onApply.
 */
export default function ProfilePhotoEditor({ file, open, onClose, onApply }) {
  const [img, setImg] = useState(null);         // HTMLImageElement
  const [face, setFace] = useState(null);       // {x,y,w,h} face box in natural coords
  const [detecting, setDetecting] = useState(false);
  const [detector, setDetector] = useState("none"); // 'native' | 'faceapi' | 'none'
  const [zoom, setZoom] = useState(1);          // 1.0 = tightest fit to face (or image square)
  const [offset, setOffset] = useState({ x: 0, y: 0 });  // in fraction of crop size
  const [busy, setBusy] = useState(false);
  const srcUrlRef = useRef(null);

  // Load file → HTMLImageElement
  useEffect(() => {
    if (!file || !open) return;
    const url = URL.createObjectURL(file);
    srcUrlRef.current = url;
    const el = new Image();
    el.onload = () => {
      setImg(el);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      detectFace(el);
    };
    el.onerror = () => toast.error("Could not read that image.");
    el.src = url;
    return () => {
      URL.revokeObjectURL(url);
      srcUrlRef.current = null;
    };
  }, [file, open]);

  const detectFace = async (el) => {
    setDetecting(true);
    setFace(null);
    setDetector("none");
    // 1. Native (Chrome / Edge / Samsung Internet)
    try {
      if (typeof window !== "undefined" && "FaceDetector" in window) {
        const d = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
        const faces = await d.detect(el);
        if (faces?.length > 0) {
          const b = faces[0].boundingBox;
          setFace({ x: b.x, y: b.y, w: b.width, h: b.height });
          setDetector("native");
          setDetecting(false);
          return;
        }
      }
    } catch (e) { /* fall through */ }
    // 2. face-api.js fallback (Safari / Firefox / iOS)
    try {
      const faceapi = await loadFaceApi();
      const result = await faceapi.detectSingleFace(
        el,
        new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.3 }),
      );
      if (result && result.box) {
        const b = result.box;
        setFace({ x: b.x, y: b.y, w: b.width, h: b.height });
        setDetector("faceapi");
      }
    } catch (e) {
      console.warn("face-api fallback failed:", e);
    }
    setDetecting(false);
  };

  // Compute the current crop rectangle (in natural image coords)
  const computeCrop = () => {
    if (!img) return null;
    const iw = img.naturalWidth, ih = img.naturalHeight;
    let cx, cy, base;
    if (face) {
      // Center the crop on the face; base crop = 1.6× the face width (leaves room for hair + shoulders)
      cx = face.x + face.w / 2;
      cy = face.y + face.h / 2;
      base = Math.max(face.w, face.h) * 1.6;
    } else {
      // No face → center of image, base = min(width, height)
      cx = iw / 2;
      cy = ih / 2;
      base = Math.min(iw, ih);
    }
    // Zoom adjusts the crop size (smaller size = more zoomed-in view)
    const size = Math.min(iw, ih, base / zoom);
    const nudgeX = offset.x * size;
    const nudgeY = offset.y * size;
    let sx = cx - size / 2 + nudgeX;
    let sy = cy - size / 2 + nudgeY;
    sx = Math.max(0, Math.min(sx, iw - size));
    sy = Math.max(0, Math.min(sy, ih - size));
    return { sx, sy, size };
  };

  const renderPreview = (canvas) => {
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    const c = computeCrop();
    if (!c) return;
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.drawImage(img, c.sx, c.sy, c.size, c.size, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  };

  const previewCanvasRef = useRef(null);
  useEffect(() => {
    renderPreview(previewCanvasRef.current);
  }, [img, face, zoom, offset]); // eslint-disable-line

  // Render the full original with the face box + crop box overlay
  const overlayCanvasRef = useRef(null);
  useEffect(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas || !img) return;
    const maxW = 340, maxH = 340;
    const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
    canvas.width = img.naturalWidth * scale;
    canvas.height = img.naturalHeight * scale;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const c = computeCrop();
    if (c) {
      ctx.strokeStyle = "rgba(30,64,175,0.9)";
      ctx.lineWidth = 3;
      ctx.setLineDash([]);
      ctx.strokeRect(c.sx * scale, c.sy * scale, c.size * scale, c.size * scale);
    }
    if (face) {
      ctx.strokeStyle = "rgba(16,185,129,0.9)";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(face.x * scale, face.y * scale, face.w * scale, face.h * scale);
    }
  }, [img, face, zoom, offset]);

  const nudge = (dx, dy) => setOffset(o => ({
    x: Math.max(-0.5, Math.min(0.5, o.x + dx)),
    y: Math.max(-0.5, Math.min(0.5, o.y + dy)),
  }));

  const reset = () => { setZoom(1); setOffset({ x: 0, y: 0 }); };

  const apply = () => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    setBusy(true);
    canvas.toBlob((blob) => {
      setBusy(false);
      if (!blob) { toast.error("Could not process image."); return; }
      onApply(blob);
    }, "image/jpeg", 0.9);
  };

  const faceDetectorSupported = true; // retained for backward compatibility

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="photo-editor-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-700" /> Perfect your profile photo
          </DialogTitle>
        </DialogHeader>

        {detector === "none" && !detecting && !face && (
          <div className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2" data-testid="detector-fallback-notice">
            No face detected. The crop is centred on the image — use the nudge arrows or zoom if you need to reframe.
          </div>
        )}
        {detector === "faceapi" && face && (
          <div className="text-xs rounded-md border border-blue-200 bg-blue-50 text-blue-800 px-3 py-2" data-testid="detector-faceapi-notice">
            <Sparkles className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
            Face detected via the offline fallback detector (first load was ~300 KB — cached from now on).
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-5 items-start">
          {/* Original with overlays */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex justify-center">
            <canvas ref={overlayCanvasRef} className="max-w-full h-auto rounded-md" data-testid="photo-editor-overlay" />
          </div>

          {/* Square preview + status */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Final preview</div>
            <div className="mt-1 rounded-xl border border-slate-200 bg-white p-2 grid place-items-center">
              <canvas ref={previewCanvasRef} className="w-44 h-44 rounded-lg" data-testid="photo-editor-preview" />
            </div>
            <div className="mt-2 text-xs">
              {detecting
                ? <span className="text-slate-500">Detecting face…</span>
                : face
                  ? <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="face-ok-badge">
                      <Check className="w-3.5 h-3.5" /> Face detected
                    </span>
                  : <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid="face-none-badge">
                      <AlertTriangle className="w-3.5 h-3.5" /> No face detected
                    </span>
              }
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-2 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.max(0.6, +(z - ZOOM_STEP).toFixed(2)))} data-testid="zoom-out-btn">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <div className="text-xs text-slate-500 w-12 text-center font-mono">{Math.round(zoom * 100)}%</div>
            <Button size="sm" variant="outline" onClick={() => setZoom(z => Math.min(2.5, +(z + ZOOM_STEP).toFixed(2)))} data-testid="zoom-in-btn">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <div className="w-px h-6 bg-slate-200 mx-2" />
            <Button size="sm" variant="outline" onClick={() => nudge(0, -NUDGE_PX_FRAC)} data-testid="nudge-up"><ArrowUp className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => nudge(0, NUDGE_PX_FRAC)} data-testid="nudge-down"><ArrowDown className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => nudge(-NUDGE_PX_FRAC, 0)} data-testid="nudge-left"><ArrowLeft className="w-4 h-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => nudge(NUDGE_PX_FRAC, 0)} data-testid="nudge-right"><ArrowRight className="w-4 h-4" /></Button>
            <div className="w-px h-6 bg-slate-200 mx-2" />
            <Button size="sm" variant="ghost" onClick={reset} data-testid="reset-crop-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="photo-editor-cancel">Cancel</Button>
          <Button onClick={apply} disabled={busy || !img} className="bg-blue-700 hover:bg-blue-800" data-testid="photo-editor-apply">
            <Check className="w-4 h-4 mr-1" /> {busy ? "Processing…" : "Use this photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
